import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Input,
    Modal,
    Skeleton,
    Space,
    Tag,
    Typography,
    message,
} from 'antd';
import { useAuth } from '../../../hooks/useAuth';
import {
    aiAdminApi,
    SubsystemDetail,
    SubsystemDiff,
} from '../../../services/ai-admin-api';
import { useSubsystemEditorContext } from '../hooks/useSubsystemDraftEditor';
import { DiffViewer } from './DiffViewer';

const { Text } = Typography;

/**
 * Reduce a SubsystemDiff to a one-glance summary: "3 system_settings changed,
 * 2 advice_ai_config rows updated, 1 added" — so the admin can verify scope
 * without reading the full structured diff. The DiffViewer below still shows
 * the per-field detail.
 */
function diffSummary(diff: SubsystemDiff | null): {
    headline: string;
    chips: Array<{ key: string; label: string; color: string }>;
} {
    if (!diff || !diff.hasChanges) {
        return { headline: 'No changes detected.', chips: [] };
    }
    const chips: Array<{ key: string; label: string; color: string }> = [];
    if (diff.systemSettings.length > 0) {
        chips.push({
            key: 'settings',
            label: `${diff.systemSettings.length} system_settings changed`,
            color: 'blue',
        });
    }
    for (const [table, rows] of Object.entries(diff.configTableRows ?? {})) {
        if (!rows || rows.length === 0) continue;
        const added = rows.filter((r) => r.kind === 'added').length;
        const removed = rows.filter((r) => r.kind === 'removed').length;
        const changed = rows.filter((r) => r.kind === 'changed').length;
        const parts: string[] = [];
        if (added) parts.push(`${added} added`);
        if (changed) parts.push(`${changed} changed`);
        if (removed) parts.push(`${removed} removed`);
        chips.push({
            key: table,
            label: `${table}: ${parts.join(', ')}`,
            color: removed > 0 ? 'red' : added > 0 ? 'green' : 'orange',
        });
    }
    const total =
        diff.systemSettings.length +
        Object.values(diff.configTableRows ?? {}).reduce(
            (n, rows) => n + (rows?.length ?? 0),
            0,
        );
    return {
        headline: `${total} change${total === 1 ? '' : 's'} pending publish.`,
        chips,
    };
}

interface Props {
    open: boolean;
    detail: SubsystemDetail | null;
    onCancel: () => void;
}

/**
 * Pre-publish confirmation modal. Loads the structured diff between the
 * subsystem's currently-published version and its pending draft, lets the
 * admin add notes, then calls editor.publish() — which centralises the POST,
 * the success toast, RTK Query cache-tag invalidation for legacy /dashboard
 * consumers, and the post-publish reload. Doing those here separately would
 * fan out into duplicate reloads and skip the RTK invalidations entirely.
 */
export const PublishConfirmModal: React.FC<Props> = ({
    open,
    detail,
    onCancel,
}) => {
    const { user } = useAuth();
    const jwt = user?.jwtToken ?? '';
    const editor = useSubsystemEditorContext();
    const [diff, setDiff] = useState<SubsystemDiff | null>(null);
    const [notes, setNotes] = useState('');
    const [isLoadingDiff, setIsLoadingDiff] = useState(false);
    const summary = useMemo(() => diffSummary(diff), [diff]);

    useEffect(() => {
        if (!open || !detail || !detail.draftVersionId || !detail.currentPublishedVersionId) {
            setDiff(null);
            return;
        }
        setIsLoadingDiff(true);
        aiAdminApi
            .diffVersions(
                jwt,
                detail.id,
                detail.currentPublishedVersionId,
                detail.draftVersionId,
            )
            .then(setDiff)
            .catch((err) => message.error(err.message || 'Failed to compute diff'))
            .finally(() => setIsLoadingDiff(false));
    }, [open, detail, jwt]);

    const handlePublish = async () => {
        if (!detail) return;
        await editor.publish(notes || undefined);
        setNotes('');
        onCancel();
    };

    return (
        <Modal
            open={open}
            title={detail ? `Publish "${detail.displayName}"?` : 'Publish'}
            okText="Publish"
            confirmLoading={editor.isPublishing}
            onCancel={onCancel}
            onOk={handlePublish}
            width={900}
            destroyOnClose
        >
            <Text type="secondary">
                Review the diff below before publishing. The new version will be applied to live tables atomically and the relevant AI response caches will be invalidated.
            </Text>
            {!isLoadingDiff && diff && (
                <Alert
                    type={diff.hasChanges ? 'info' : 'warning'}
                    showIcon
                    message={summary.headline}
                    description={
                        summary.chips.length > 0 ? (
                            <Space size="small" wrap>
                                {summary.chips.map((chip) => (
                                    <Tag color={chip.color} key={chip.key}>
                                        {chip.label}
                                    </Tag>
                                ))}
                            </Space>
                        ) : undefined
                    }
                    style={{ marginTop: 12 }}
                />
            )}
            <div style={{ marginTop: 12 }}>
                {isLoadingDiff ? <Skeleton active /> : <DiffViewer diff={diff} />}
            </div>
            <div style={{ marginTop: 16 }}>
                <Text strong>Publish notes (optional)</Text>
                <Input.TextArea
                    rows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="What changed and why?"
                    style={{ marginTop: 4 }}
                />
            </div>
        </Modal>
    );
};
